import type { OptionType } from '@/types'

import { isMailSourceType } from '../../lib/stockImportsDefaults'
import type { ConfigFormInput } from './configFormModel'

export type ConfigReviewItem = {
  label: string
  value: string | string[]
}

export type ConfigReviewSection = {
  title: string
  description: string
  items: ConfigReviewItem[]
}

type ReviewOptions = {
  isSelfSupplierMode: boolean
  supplierOptions: OptionType[]
  storeOptions: OptionType[]
  sourceTypeOptions: OptionType[]
  cronOptions: OptionType[]
  brandOptions: OptionType[]
}

export const getColumnName = (value: string) => {
  const columnNumber = Number(value)
  if (value.trim() === '' || !Number.isInteger(columnNumber)) return '—'

  let rest = columnNumber
  let name = ''
  while (rest > 0) {
    const remainder = (rest - 1) % 26
    name = String.fromCharCode(65 + remainder) + name
    rest = Math.floor((rest - 1) / 26)
  }
  return `${columnNumber} (${name})`
}

const findLabel = (options: OptionType[], value: string) =>
  options.find((option) => option.value === value)?.label ?? (value || '—')

const orDefault = (value: string, fallback: string) =>
  value.trim() === '' ? fallback : value

const percent = (value: string) =>
  value.trim() === '' ? 'Не задано' : `${value}%`

const listValue = (items: string[]) =>
  items.length > 0 ? items.join(', ') : 'Не указано'

const getSourceValue = (values: ConfigFormInput): string | string[] => {
  if (values.sourceType === 'URL')
    return orDefault(values.url, 'Ссылка не указана')
  if (values.sourceType === 'CUSTOM_IMAP')
    return [
      `Логин почты — ${orDefault(values.imapLogin, 'не указан')}`,
      `Имя или маска файла — ${orDefault(values.fileMask, 'не указана')}`,
    ]
  return [
    `Допустимый отправитель: ${orDefault(values.expectedSender, 'не указан')}`,
    `Маркер темы письма: ${orDefault(values.subjectMask, 'не указан')}`,
    `Имя или маска файла: ${orDefault(values.fileMask, 'не указана')}`,
  ]
}

const getSheetValue = (values: ConfigFormInput) => {
  if (values.sheetSelectionType === 'BY_NAME')
    return orDefault(values.sheetNames, 'Не указано')
  if (values.sheetSelectionType === 'BY_INDEX')
    return orDefault(values.sheetIndexes, 'Не указано')
  return 'Все листы'
}

const getBrandValue = (
  values: ConfigFormInput,
  brandOptions: OptionType[]
): string | string[] => {
  const fallback = values.fallbackBrandId
    ? `Бренд по умолчанию: ${findLabel(brandOptions, values.fallbackBrandId)}`
    : undefined
  if (values.brandColumnNumber.trim() !== '')
    return getColumnName(values.brandColumnNumber)
  if (values.detectBrandInDescription)
    return fallback
      ? ['Ищется в наименовании', fallback]
      : 'Ищется в наименовании'
  return fallback ?? 'Не указан'
}

export const buildConfigReviewSections = (
  values: ConfigFormInput,
  options: ReviewOptions
): ConfigReviewSection[] => [
  {
    title: 'Источник и запуск',
    description:
      'Откуда система будет брать файл и как часто проверять обновление.',
    items: [
      {
        label: 'Поставщик',
        value: options.isSelfSupplierMode
          ? 'Моя организация'
          : findLabel(options.supplierOptions, values.supplierId),
      },
      {
        label: 'Склад',
        value: findLabel(options.storeOptions, values.storeId),
      },
      {
        label: 'Источник',
        value: findLabel(options.sourceTypeOptions, values.sourceType),
      },
      { label: 'Формат файла', value: values.fileFormat },
      {
        label:
          values.sourceType === 'URL' ? 'Публичная ссылка' : 'Почтовые условия',
        value: getSourceValue(values),
      },
      {
        label: 'Периодичность проверки',
        value:
          values.sourceType === 'EMAIL'
            ? 'Непрерывная проверка почты'
            : findLabel(options.cronOptions, values.cronExpression),
      },
    ],
  },
  {
    title: 'Как читать файл',
    description: 'Какие строки и листы попадут в обработку.',
    items: [
      {
        label: 'Диапазон строк',
        value: `${orDefault(values.startRowIndex, 'начало не указано')} — ${orDefault(values.endRowIndex, 'до конца файла')}`,
      },
      { label: 'Листы Excel', value: getSheetValue(values) },
    ],
  },
  {
    title: 'Колонки',
    description: 'Номера колонок начинаются с единицы: A = 1, B = 2, C = 3.',
    items: [
      {
        label: 'Артикул (производителя)',
        value: getColumnName(values.articleColumnNumber),
      },
      {
        label: 'Артикул (поставщика)',
        value: getColumnName(values.articleInchColumnNumber),
      },
      { label: 'Штрихкод', value: getColumnName(values.barcodeColumnNumber) },
      {
        label: 'Наименование',
        value: getColumnName(values.descriptionColumnNumber),
      },
      { label: 'Бренд', value: getBrandValue(values, options.brandOptions) },
      { label: 'Остаток', value: getColumnName(values.quantityColumnNumber) },
    ],
  },
  {
    title: 'Фильтры и правила',
    description: 'Ограничения и преобразования перед загрузкой остатков.',
    items: [
      { label: 'Разрешенные бренды', value: listValue(values.allowedBrands) },
      { label: 'Исключенные бренды', value: listValue(values.ignoredBrands) },
      {
        label: 'Макс. незагруженных',
        value: percent(values.maxAllowedMissingPercentage),
      },
      {
        label: 'Резкое увеличение Missing SKU',
        value: percent(values.missingPercentageSpikeAlert),
      },
      {
        label: 'Мин. распознанных от обычного',
        value: percent(values.minLoadedPercentOfMedian),
      },
      {
        label: 'Макс. строк без количества',
        value: percent(values.maxInvalidQuantityPercentage),
      },
      ...(isMailSourceType(values.sourceType)
        ? [
            {
              label: 'Нет писем, рабочих дней',
              value: orDefault(values.stalledAlertBusinessDays, 'Не задано'),
            },
          ]
        : []),
      {
        label: 'Ожидаемый заголовок колонки количества',
        value: orDefault(values.expectedQuantityHeader, 'Не задан'),
      },
      {
        label: 'Текстовые остатки в числа',
        value:
          values.textQuantityMapping.length > 0
            ? String(values.textQuantityMapping.length)
            : 'Нет',
      },
    ],
  },
]
